import { Home06Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  BoomFinanceVerificationSettings,
  FieldDisplaySetting
} from "shared/types";
import { Show } from "shared/components";
import { formatToCurrency } from "shared/utils/currency";
import { Section } from "views/applications-id-report/components/Section";
import { Row } from "views/applications-id-report/components/Row";
import { SectionIcon } from "../components/SectionIcon";
import { PurchaseInfo } from "../types";
import { formatLabel, isFieldVisible } from "../utils";

const toText = (value?: number | string | null): string =>
  value || value === 0 ? String(value) : "";

const getDimensionsRow = (
  loan: PurchaseInfo,
  showLength: boolean,
  showWidth: boolean
): { title: string; value: string } | null => {
  if (showLength && showWidth)
    return {
      title: "Dimensions",
      value:
        loan.houseLength && loan.houseWidth
          ? `${loan.houseLength}' × ${loan.houseWidth}'`
          : ""
    };
  if (showLength)
    return { title: "Length (ft)", value: toText(loan.houseLength) };
  if (showWidth) return { title: "Width (ft)", value: toText(loan.houseWidth) };
  return null;
};

export const HomeDetailsSlice = ({
  loan,
  settings
}: {
  loan: PurchaseInfo;
  settings?: BoomFinanceVerificationSettings["home_details"];
}) => {
  const dimensionsRow = getDimensionsRow(
    loan,
    isFieldVisible(loan.houseLength, settings?.house_length),
    isFieldVisible(loan.houseWidth, settings?.house_width)
  );
  const hasCapturedData = Boolean(
    loan.homeModel ||
    loan.manufacturer ||
    loan.homeSerial ||
    loan.yearBuilt ||
    loan.houseLength ||
    loan.houseWidth ||
    loan.homeConditionType ||
    loan.landType ||
    loan.lotRent
  );

  const stepEnabled = settings?.is_known !== FieldDisplaySetting.Hidden;

  if (!stepEnabled && !hasCapturedData) return null;

  return (
    <Section
      sectionTitle="Home details"
      icon={<SectionIcon icon={Home06Icon} />}
    >
      <div className="space-y-2">
        <Show if={loan.homeDetailsKnown === false}>
          <Row title="Details known" value="No" />
        </Show>

        <Show if={loan.homeDetailsKnown !== false}>
          <div className="space-y-2">
            <Show
              if={isFieldVisible(loan.manufacturer, settings?.manufacturer)}
            >
              <Row title="Manufacturer" value={loan.manufacturer} />
            </Show>
            <Show if={isFieldVisible(loan.homeModel, settings?.home_model)}>
              <Row title="Model" value={loan.homeModel} />
            </Show>
            <Show if={isFieldVisible(loan.homeSerial, settings?.home_serial)}>
              <Row title="Serial number" value={loan.homeSerial} />
            </Show>
            <Show if={isFieldVisible(loan.yearBuilt, settings?.year_built)}>
              <Row title="Year built" value={toText(loan.yearBuilt)} />
            </Show>
            {dimensionsRow ? (
              <Row title={dimensionsRow.title} value={dimensionsRow.value} />
            ) : null}
            <Show
              if={isFieldVisible(
                loan.homeConditionType,
                settings?.home_condition_type
              )}
            >
              <Row
                title="Condition"
                value={formatLabel(loan.homeConditionType)}
              />
            </Show>
            <Show if={isFieldVisible(loan.landType, settings?.land_type)}>
              <Row title="Land type" value={formatLabel(loan.landType)} />
            </Show>
            <Show if={isFieldVisible(loan.lotRent, settings?.lot_rent)}>
              <Row
                title="Monthly rent amount"
                value={formatToCurrency(loan.lotRent)}
              />
            </Show>
          </div>
        </Show>
      </div>
    </Section>
  );
};
